import { useState } from "react";
import { Loader2, RotateCcw, ShieldAlert } from "lucide-react";
import { Button } from "@wa/ui";
import { grantedModules, type PortalModuleGrant } from "@wa/core/portal-modules";
import { useT } from "../../../i18n/I18nProvider";
import { setContactPortalModules, type Contact } from "../../../lib/clients";

type T = ReturnType<typeof useT>;

/**
 * The people this company's grant has emptied, and the one click that undoes it.
 *
 * This list is not polish. It is the mitigation for the one rule in the whole
 * three-level design that can take access away from somebody the operator never
 * touched, and without it that consequence is invisible on every screen.
 *
 * ── HOW SOMEBODY GETS ONTO THIS LIST ───────────────────────────────────────
 *
 * The contact level INTERSECTS the company level (`resolveModuleGrant`), which
 * is what makes a company grant of `[]` a real kill switch instead of a control
 * that loses to every contact holding an explicit array. The same asymmetry has
 * a second effect nobody asks for: narrow the company to `["invoices"]` while a
 * contact's own row says `["support"]`, and the intersection is empty. That
 * person keeps their sign-in, their invitation, their badge on the contacts tab,
 * and opens nothing at all.
 *
 * Every part of that is correct under the rule. None of it produces an error, a
 * log line or a changed pixel anywhere else: the portal answers 200 with no
 * tabs, which is a legal state the shell renders in silence. The discovery route
 * is the customer telephoning. So the screen that CAUSED it says so, on the same
 * page, straight after the save that did it.
 *
 * ── IT READS THE SAVED STATE, NOT THE DRAFT ────────────────────────────────
 *
 * The panel above could predict this list from its unsaved pills - the rule is
 * `resolveModuleGrant` in `@wa/core` and the browser may call it. It does not,
 * because a prediction drawn in the same red box as a fact is a prediction the
 * reader will act on as a fact, and "these people cannot open anything" and
 * "these people would not be able to" want opposite responses. So the box states
 * only what is true right now: `portalModulesEffective` as the server resolved
 * it. The parent reloads the client after every save, so the list appears one
 * beat after the change that created it, which is the right beat.
 *
 * ── WHEN CLEARING A ROW CANNOT BE THE ANSWER ───────────────────────────────
 *
 * If the company itself opens nothing, every contact is empty and no per-person
 * clear can help, because there is nothing above to fall back to. Offering the
 * button there would be a control that swallows the click and leaves the row
 * exactly as red as before, which teaches the operator to distrust the next
 * button. So that case gets a sentence pointing at the grant above instead.
 */
export function PortalShutOutList({ t, clientId, contacts, companyGrant, canManage, onChange }: {
  t: T;
  clientId: string;
  /** The client's people as the server last sent them, `portalModulesEffective`
   *  included. Rows from a write route do not carry that field - see below. */
  contacts: Contact[];
  /**
   * The SAVED company grant these rows were resolved against, CRM-wide default
   * already folded in. Read only to answer "is there anything left to inherit",
   * never to recompute a row's own effective set.
   */
  companyGrant: PortalModuleGrant;
  canManage: boolean;
  onChange: () => void;
}) {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [failedId, setFailedId] = useState<string | null>(null);

  // `Array.isArray` rather than a length check on a possibly missing field: an
  // absent `portalModulesEffective` means the route did not send one, which is
  // not the same claim as "opens nothing". Treating the two alike would paint
  // every contact red against an older build.
  const shutOut = contacts.filter(
    (c) => Array.isArray(c.portalModulesEffective) && c.portalModulesEffective.length === 0,
  );
  if (shutOut.length === 0) return null;

  const companyOpensNothing = grantedModules(companyGrant).length === 0;

  async function clearOverride(contactId: string) {
    setBusyId(contactId);
    setFailedId(null);
    try {
      await setContactPortalModules(clientId, contactId, null);
      onChange();
    } catch {
      setFailedId(contactId);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="rounded-lg border border-red-300 bg-red-50/60 p-3 dark:border-red-900 dark:bg-red-950/30">
      <p className="flex items-center gap-1.5 text-sm font-medium text-red-700 dark:text-red-300">
        <ShieldAlert className="size-4 shrink-0" />
        {t("clients.portal.shutOutTitle")}
      </p>
      <p className="mt-1 text-xs text-red-700/80 dark:text-red-300/80">
        {companyOpensNothing ? t("clients.portal.shutOutCompany") : t("clients.portal.shutOutBody")}
      </p>

      <ul className="mt-2 space-y-1">
        {shutOut.map((c) => {
          const name = [c.firstName, c.lastName].filter(Boolean).join(" ");
          // Whether this person is actually holding a key. A contact who was
          // never invited is shut out of a portal they could not open anyway, so
          // the row says so rather than reading as an outage. It stays on the
          // list all the same: grants are writable before the invite goes out on
          // purpose, and an invitation sent to somebody already emptied is the
          // exact mistake this list exists to catch.
          const hasKey = Boolean(c.portalUser) || c.portalInvitePending === true;
          return (
            <li key={c.id} className="flex flex-wrap items-center gap-2 text-xs">
              <span className="font-medium text-red-800 dark:text-red-200">{name || c.email || c.id}</span>
              {!hasKey && (
                <span className="text-red-700/60 dark:text-red-300/60">{t("clients.portal.shutOutNoKey")}</span>
              )}
              {canManage && !companyOpensNothing && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-6 gap-1 px-2 text-[11px]"
                  disabled={busyId === c.id}
                  onClick={() => void clearOverride(c.id)}
                >
                  {busyId === c.id ? <Loader2 className="size-3 animate-spin" /> : <RotateCcw className="size-3" />}
                  {t("clients.portal.shutOutClear")}
                </Button>
              )}
              {failedId === c.id && (
                <span className="text-red-600 dark:text-red-400">{t("clients.portal.shutOutFailed")}</span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
